/**
 * Service to handle Google Tag Manager initialization
 * GTM will only be loaded after user consent via Usercentrics
 * 
 */

import { UsercentricsConsentService } from './UsercentricsConsentService';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

export class GoogleTagManagerService {
  private static GTM_ID = 'GTM-5696G4G';
  private static isInitialized = false;
  private static unsubscribe: (() => void) | null = null;

  /**
   * Initialize GTM service and listen for Usercentrics consent
   * GTM will load when user consents to "Google Analytics" in "marketing" category
   */
  public static initialize(): void {
    if (this.isInitialized) {
      return;
    }

    if (UsercentricsConsentService.hasConsent('Google Analytics', 'marketing')) {
      this.loadGTM();
      return;
    }
    
    this.unsubscribe = UsercentricsConsentService.onConsentChange(
      'Google Analytics',
      'marketing',
      (hasConsent: boolean) => {
        if (hasConsent) {
          if (!this.isInitialized) {
            this.loadGTM();
          }
          // If already initialized, GTM is already loaded and will resume tracking
        } else if (!hasConsent && this.isInitialized) {
          // Consent was revoked - stop tracking
          this.handleConsentRevocation();
        }
      }
    );
  }

  /**
   * Cleanup - unregister consent listener
   */
  public static cleanup(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Handle consent revocation - stop GTM tracking
   * Note: GTM script cannot be fully removed once loaded, but we can disable tracking
   */
  private static handleConsentRevocation(): void {
    // Clear dataLayer to stop future tracking events
    if (window.dataLayer) {
      window.dataLayer = [];
    }
    
    // Note: GTM script and noscript iframe remain in DOM but tracking is disabled
    // If consent is granted again later, GTM is already loaded so we don't need to reload it
    // We keep isInitialized = true to prevent attempting to reload the script
  }

  /**
   * Load Google Tag Manager script and noscript iframe
   */
  private static loadGTM(): void {
    if (this.isInitialized) {
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.GTM_ID}`;
    script.onerror = () => {
      console.error('Failed to load Google Tag Manager');
    };

    // Insert script before first script tag or at end of head
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    // Add noscript iframe to body
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.GTM_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    iframe.title = 'gtag';
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);

    this.isInitialized = true;
  }
}
